import { jsx } from 'react/jsx-runtime';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { createContext, useRef, useEffect, useMemo, useContext } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __values(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var PageContext = createContext(null);

var TrackContext = createContext(null);

function filterByUndefined(value) {
    return typeof value === 'undefined';
}

function mapValueToIsoDate(value) {
    if (typeof value === 'undefined') {
        return;
    }
    if (typeof value == 'number') {
        return new Date(value).toISOString();
    }
    if (typeof value === 'string') {
        return value;
    }
    return value.toISOString();
}

function mapValueToString(value) {
    if (typeof value === 'undefined') {
        return;
    }
    if (typeof value === 'number') {
        return value.toString();
    }
    return value;
}

var OBJECT_TYPE_ERROR = new Error('Expected value to be an object.');

function parseStringifiedObject(str) {
    var parsedStr = JSON.parse(str);
    if (typeof parsedStr !== 'object' || parsedStr === null) {
        throw OBJECT_TYPE_ERROR;
    }
    return parsedStr;
}

var DEFAULT_ADDRESS_TRAIT = Object.freeze({});
var DEFAULT_COMPANY_TRAIT = Object.freeze({});
var DEFAULT_TRAITS = Object.freeze({});
function useIdentify(analytics, traits) {
    var _a = traits !== null && traits !== void 0 ? traits : DEFAULT_TRAITS, address = _a.address, age = _a.age, avatar = _a.avatar, birthday = _a.birthday, company = _a.company, createdAt = _a.createdAt, description = _a.description, email = _a.email, firstName = _a.firstName, gender = _a.gender, id = _a.id, lastName = _a.lastName, name = _a.name, phone = _a.phone, title = _a.title, username = _a.username, website = _a.website, restTraits = __rest(_a, ["address", "age", "avatar", "birthday", "company", "createdAt", "description", "email", "firstName", "gender", "id", "lastName", "name", "phone", "title", "username", "website"]);
    var _b = address !== null && address !== void 0 ? address : DEFAULT_ADDRESS_TRAIT, addressCity = _b.city, addressCountry = _b.country, addressPostalCode = _b.postalCode, addressState = _b.state, addressStreet = _b.street;
    var _c = company !== null && company !== void 0 ? company : DEFAULT_COMPANY_TRAIT, companyEmployeeCount = _c.employeeCount, companyId = _c.id, companyIndustry = _c.industry, companyName = _c.name, companyPlan = _c.plan;
    var restTraitsStringified = JSON.stringify(restTraits);
    var asyncIdentifyRef = useRef(null);
    useEffect(function () {
        var getAddress = function () {
            var addressSubtraits = [
                addressCity,
                addressCountry,
                addressPostalCode,
                addressState,
                addressStreet,
            ];
            if (addressSubtraits.every(filterByUndefined)) {
                return;
            }
            return {
                city: addressCity,
                country: addressCountry,
                postalCode: addressPostalCode,
                state: addressState,
                street: addressStreet,
            };
        };
        var getCompany = function () {
            var companySubtraits = [
                companyEmployeeCount,
                companyId,
                companyIndustry,
                companyName,
                companyPlan,
            ];
            if (companySubtraits.every(filterByUndefined)) {
                return;
            }
            return {
                employeeCount: companyEmployeeCount,
                id: companyId,
                industry: companyIndustry,
                name: companyName,
                plan: companyPlan,
            };
        };
        var identifyId = mapValueToString(id);
        var restTraitsParsed = parseStringifiedObject(restTraitsStringified);
        var identifyTraits = __assign(__assign({}, restTraitsParsed), { address: getAddress(), age: age, avatar: avatar, birthday: mapValueToIsoDate(birthday), company: getCompany(), createdAt: mapValueToIsoDate(createdAt), description: description, email: email, firstName: firstName, gender: gender, id: identifyId, lastName: lastName, name: name, phone: mapValueToString(phone), title: title, username: username, website: website });
        if (Object.values(identifyTraits).every(filterByUndefined)) {
            return;
        }
        asyncIdentifyRef.current = analytics.identify(identifyId, identifyTraits);
    }, [
        addressCity,
        addressCountry,
        addressPostalCode,
        addressState,
        addressStreet,
        age,
        analytics,
        avatar,
        birthday,
        companyEmployeeCount,
        companyId,
        companyIndustry,
        companyName,
        companyPlan,
        createdAt,
        description,
        email,
        firstName,
        gender,
        id,
        lastName,
        name,
        phone,
        restTraitsStringified,
        title,
        username,
        website,
    ]);
    return asyncIdentifyRef;
}

function filterByRecord(value) {
    return typeof value === 'object' && value !== null;
}

function sortRecordKeys(a, b) {
    return a.toString().localeCompare(b.toString());
}

var DEFAULT_DEPTH = 1;
var DEPTH_INCREMENT = 1;
function mapObjectToDependencyArray(obj) {
    function createPrefixedDependencyArray(objInner, depth, prefix) {
        var e_1, _a;
        var keys = Object.keys(objInner);
        keys.sort(sortRecordKeys);
        var dependencies = [];
        try {
            for (var keys_1 = __values(keys), keys_1_1 = keys_1.next(); !keys_1_1.done; keys_1_1 = keys_1.next()) {
                var key = keys_1_1.value;
                var keyStr = key.toString();
                dependencies.push("".concat(prefix, ".").concat(depth, ".").concat(keyStr));
                var value = objInner[key];
                if (filterByRecord(value)) {
                    var newDependencies = createPrefixedDependencyArray(value, depth + DEPTH_INCREMENT, "".concat(prefix, ".").concat(depth, ".").concat(keyStr));
                    dependencies.push.apply(dependencies, __spreadArray([], __read(newDependencies), false));
                }
                else {
                    dependencies.push(value);
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (keys_1_1 && !keys_1_1.done && (_a = keys_1.return)) _a.call(keys_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return dependencies;
    }
    return createPrefixedDependencyArray(obj, DEFAULT_DEPTH, 'root');
}

function useSegment(_a) {
    var AnalyticsBrowser = _a.AnalyticsBrowser, traits = _a.traits, rest = __rest(_a, ["AnalyticsBrowser", "traits"]);
    var loadDependencies = mapObjectToDependencyArray(rest);
    var analytics = useMemo(function () {
        var cdnSettings = rest.cdnSettings, cdnURL = rest.cdnURL, plugins = rest.plugins, timeout = rest.timeout, writeKey = rest.writeKey, initOptions = __rest(rest, ["cdnSettings", "cdnURL", "plugins", "timeout", "writeKey"]);
        var analyticsSettings = {
            cdnSettings: cdnSettings,
            cdnURL: cdnURL,
            plugins: plugins,
            timeout: timeout,
            writeKey: writeKey,
        };
        return AnalyticsBrowser.load(analyticsSettings, initOptions);
    }, __spreadArray([AnalyticsBrowser], __read(loadDependencies), false));
    useIdentify(analytics, traits);
    return {
        page: analytics.page,
        track: analytics.track,
    };
}

function Segment(_a) {
    var _b = _a.AnalyticsBrowser, AnalyticsBrowser$1 = _b === void 0 ? AnalyticsBrowser : _b, children = _a.children, traits = _a.traits, rest = __rest(_a, ["AnalyticsBrowser", "children", "traits"]);
    var _c = useSegment(__assign({ AnalyticsBrowser: AnalyticsBrowser$1, traits: traits }, rest)), page = _c.page, track = _c.track;
    return (jsx(PageContext.Provider, __assign({ value: page }, { children: jsx(TrackContext.Provider, __assign({ value: track }, { children: children })) })));
}

var MISSING_SEGMENT_CONTEXT_ERROR = new Error('Expected Segment context provider to be mounted.');

function useSegmentPage() {
    var page = useContext(PageContext);
    if (page === null) {
        throw MISSING_SEGMENT_CONTEXT_ERROR;
    }
    return page;
}

function useSegmentTrack() {
    var track = useContext(TrackContext);
    if (track === null) {
        throw MISSING_SEGMENT_CONTEXT_ERROR;
    }
    return track;
}

export { Segment as default, useSegmentPage, useSegmentTrack };
